import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WorkloadTask } from 'shovel-lib/types';

import { CalendarCourse } from '@state/calendar/types';
import { RootState } from '@state/types';
import * as workloadSelectors from '@state/workload/selectors';

import { categorizeCourse, getUncategorizedTasksForCourse } from './taskCategorization';

const MAX_IN_FLIGHT = 3;

export const matchesAutoCategorizeHeuristic = (course: CalendarCourse, tasks: WorkloadTask[]) =>
  course.needsEstimation === true &&
  (course.taskCategories?.length ?? 0) === 0 &&
  getUncategorizedTasksForCourse(tasks, course.id).length > 0;

export const useAutoTaskCategorization = (courses: CalendarCourse[], isLoggedIn: boolean) => {
  const dispatch = useDispatch();
  const tasks = useSelector((state: RootState) => state.workload.tasks);
  const categorizingCourseIds = useSelector(workloadSelectors.getCategorizingCourseIds);
  const categorizingIdSet = useMemo(() => new Set(categorizingCourseIds), [categorizingCourseIds]);
  const pendingEstimateReminders = useSelector(workloadSelectors.getPendingEstimateReminders);

  const attemptedRef = useRef<Set<number>>(new Set());
  const failedRef = useRef<Set<number>>(new Set());
  const inFlightRef = useRef(0);
  const queueRef = useRef<number[]>([]);
  const coursesRef = useRef(courses);
  const tasksRef = useRef(tasks);
  const startNextRef = useRef<() => void>(() => undefined);

  coursesRef.current = courses;
  tasksRef.current = tasks;

  useEffect(() => {
    return () => {
      queueRef.current = [];
    };
  }, []);

  startNextRef.current = () => {
    while (inFlightRef.current < MAX_IN_FLIGHT && queueRef.current.length > 0) {
      const courseId = queueRef.current.shift();
      if (courseId == null) break;
      inFlightRef.current += 1;

      void (async () => {
        try {
          const course = coursesRef.current.find(c => c.id === courseId);
          if (!course) {
            failedRef.current.add(courseId);
            return;
          }
          const ok = await categorizeCourse(dispatch, course, tasksRef.current);
          if (!ok) {
            failedRef.current.add(courseId);
          }
        } finally {
          inFlightRef.current -= 1;
          startNextRef.current();
        }
      })();
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      attemptedRef.current = new Set();
      failedRef.current = new Set();
      queueRef.current = [];
      return;
    }

    for (const course of courses) {
      if (!matchesAutoCategorizeHeuristic(course, tasks)) continue;
      if (attemptedRef.current.has(course.id) || failedRef.current.has(course.id)) continue;
      if (categorizingIdSet.has(course.id)) continue; // a manual trigger already grabbed this course
      attemptedRef.current.add(course.id);
      queueRef.current.push(course.id);
    }

    startNextRef.current();
  }, [isLoggedIn, courses, tasks, categorizingIdSet]);

  // Reflects every in-flight run (auto-triggered or manual) so any surface showing this list
  // (the floating status panel, Pile, the task list header) stays in sync with the others.
  const categorizingCourses = useMemo(() => courses.filter(course => categorizingIdSet.has(course.id)), [
    courses,
    categorizingIdSet
  ]);

  // Also surfaces courses awaiting a nudge back into the Estimator after an
  // Estimator-triggered auto-categorize closed it (see TaskEstimatorDialog).
  const estimateCourses = useMemo(
    () =>
      courses.filter(
        course =>
          !categorizingIdSet.has(course.id) &&
          (course.needsEstimation === true || pendingEstimateReminders.includes(course.id))
      ),
    [courses, categorizingIdSet, pendingEstimateReminders]
  );

  return { categorizingCourses, estimateCourses };
};
